// One sort control for every list (owner, 2026-11-03): pick what to sort by,
// and an arrow beside it flips the direction (up = ascending: A to Z, oldest,
// lowest; down = descending). The value is the server's `sort`: `field` or
// `-field`.

import { cn } from '@/lib/format'
import { parseSort } from '@/lib/sort'
import { SelectInput } from './Field'
import { ArrowDownIcon, ArrowUpIcon } from './icons'

export interface SortField {
  value: string
  label: string
  /** The direction a pick of this field starts in: desc for dates people want newest first. */
  defaultDesc?: boolean
}

export function SortControl({
  label,
  fields,
  value,
  onChange,
  className,
}: {
  /** The select's accessible name, e.g. "Sort users". */
  label: string
  fields: readonly SortField[]
  /** `field` or `-field`. */
  value: string
  onChange: (sort: string) => void
  className?: string
}) {
  const { field, desc } = parseSort(value)
  const current = fields.find((f) => f.value === field) ?? fields[0]
  const pick = (next: string) => {
    const f = fields.find((x) => x.value === next) ?? fields[0]
    onChange(f.defaultDesc ? `-${f.value}` : f.value)
  }
  const flip = () => onChange(desc ? current.value : `-${current.value}`)
  return (
    <span className={cn('flex items-center gap-1', className)}>
      <SelectInput aria-label={label} value={current.value} options={fields.map((f) => ({ value: f.value, label: f.label }))} onChange={(e) => pick(e.target.value)} className="h-8 min-w-0 flex-1 sm:w-40" />
      <button
        type="button"
        onClick={flip}
        aria-label={desc ? 'Descending: switch to ascending' : 'Ascending: switch to descending'}
        title={desc ? 'Descending' : 'Ascending'}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line bg-panel text-muted transition-colors hover:border-line-strong hover:text-ink"
      >
        {desc ? <ArrowDownIcon size={15} /> : <ArrowUpIcon size={15} />}
      </button>
    </span>
  )
}
